import { LuxButton, LuxCheckboxInput, LuxPhotoCard, LuxPhotoFullScreen } from '@lux/wardrobe-lib'
import { FILTERS_REDUCER_NAMESPACE } from '@/app/redux/reducers/filters'
import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import FilterSubCategoryItem from '../Filters/FilterSubCategoryItem/FilterSubCategoryItem'
import Accordion from '../Layout/Accordion/Accordion'
import { Swiper, SwiperSlide } from 'swiper/react'
import { setAlbumDetails } from '@/app/redux/actions/albums'
import { GALLERY_REDUCER_NAMESPACE } from '@/app/redux/reducers/activity-creation'
import { Album } from '@Techedge/digitalportal-shared'
import { usePhotoFullScreen } from '@/app/hooks/usePhotoFullScreen'
import './Gallery.scss'

const AddedPhotosAccordion = (props: {
    addedPhotos: any[]
    albumDetails: Album
    setAlbumDetails: (a: Album) => void
}) => {
    const { addedPhotos, albumDetails, setAlbumDetails } = props

    const intl = useIntl()

    const [accordionData, setAccordionData] = useState<any[]>([])
    const [selectedPhotos, setSelectedPhotos] = useState<any[]>([])
    const [selectAll, setSelectAll] = useState<boolean>(false)

    const {
        uploadedImageList,
        setUploadedImageList,
        selectedImage,
        selectedImageInfo,
        tagList,
        newTagPosition,
        tagOnImageList,
        onCloseFullScreen,
        previousImage,
        nextImage,
        setEditLoading,
        onExpandImage,
        showPhotoFullScreen,
    } = usePhotoFullScreen({})

    useEffect(() => {
        let tmpAccordionData = []
        if (addedPhotos) {
            addedPhotos.forEach((photo) => {
                if (photo.storeId) {
                    let found = tmpAccordionData.find((store) => store.id === photo.storeId)
                    if (found) {
                        found.photos.push(photo)
                    } else {
                        tmpAccordionData.push({
                            id: photo.storeId,
                            name: photo.storeName || '',
                            photos: [photo],
                        })
                    }
                } else {
                    let sparePhotos = tmpAccordionData.find((store) => !store.id)
                    if (sparePhotos) {
                        sparePhotos.photos.push(photo)
                    } else {
                        tmpAccordionData.push({
                            id: null,
                            name: intl.formatMessage({
                                id: 'ph2.modals.album.attachments',
                                defaultMessage: 'Attachments',
                            }),
                            photos: [photo],
                        })
                    }
                }
            })
        }
        setAccordionData(tmpAccordionData)
    }, [addedPhotos])

    const checkSelected = (photoId: any) => selectedPhotos.includes(photoId) || selectAll

    const onSelection = (photoId: any) => {
        setSelectedPhotos((prevSelectedPhotos) => {
            if (prevSelectedPhotos.includes(photoId) || selectAll) {
                setSelectAll(false)
                return prevSelectedPhotos.filter((x) => x !== photoId)
            } else {
                return [...prevSelectedPhotos, photoId]
            }
        })
    }

    const onDelete = () => {
        setAlbumDetails({
            ...albumDetails,
            manualAttachments: selectAll
                ? []
                : addedPhotos.filter((item) => !selectedPhotos.includes(item.id)),
        })
        setSelectedPhotos([])
        setSelectAll(false)
    }

    return (
        <div className="manual-added-photos-accordion">
            <FilterSubCategoryItem
                type={'CUSTOM'}
                label={intl.formatMessage({
                    id: 'ph2.modals.album.added_photos',
                    defaultMessage: 'Added',
                })}
                filterPath="manualAttachments"
                customBadge={<div className="manual-attachments-badge">{addedPhotos.length}</div>}
                showBadge={addedPhotos.length > 0}
                customContent={
                    <div>
                        <div className="flex gap-4">
                            <LuxCheckboxInput
                                label={intl.formatMessage({
                                    id: 'gallery.detail.select_all_button',
                                    defaultMessage: 'Select all',
                                })}
                                value={selectAll}
                                onChangeCallback={() => setSelectAll(!selectAll)}
                                id={'select-all'}
                            />
                            <LuxButton
                                variant={'text'}
                                onClick={() => {
                                    setSelectedPhotos([])
                                    setSelectAll(false)
                                }}
                                text={intl.formatMessage({
                                    id: 'gallery.detail.cancel_selection_button',
                                    defaultMessage: 'CANCEL SELECTION',
                                })}
                            />
                        </div>
                        <div>
                            {accordionData.map((item, itemIndex) => (
                                <Accordion
                                    key={itemIndex}
                                    title={item.id ? `Store ${item.id} - ${item.name}` : item.name}
                                    className="added-photos-stores-accordion">
                                    <Swiper spaceBetween={15} slidesPerView="auto">
                                        {item.photos.map((photo, photoIndex) => {
                                            const card = (
                                                <LuxPhotoCard
                                                    photo={{
                                                        url: photo.url,
                                                        description: photo.macroarea,
                                                    }}
                                                    footerTextRight={item.id}
                                                    expandEnabled
                                                    statusIconTooltip={intl.formatMessage({
                                                        id: `ph2.gallery.detail.task_photo.status.${photo.status?.name}`,
                                                        defaultMessage: photo.status?.name,
                                                    })}
                                                    statusIcon={photo.status?.icon}
                                                    statusIconColor={photo.status?.color}
                                                    selectionActive
                                                    selectionValue={checkSelected(photo.id)}
                                                    onSelection={() => onSelection(photo.id)}
                                                    onExpand={() => {
                                                        setUploadedImageList(item.photos)
                                                        onExpandImage({
                                                            name: photo.name,
                                                            url: photo.url,
                                                        })
                                                    }}
                                                />
                                            )
                                            return (
                                                <SwiperSlide
                                                    key={photoIndex}
                                                    className="swiper-slide-added-photos"
                                                    style={{ width: '270px !important' }}>
                                                    {card}
                                                </SwiperSlide>
                                            )
                                        })}
                                    </Swiper>
                                </Accordion>
                            ))}
                        </div>
                        <span className="flex justify-content-end">
                            <LuxButton
                                variant={'text'}
                                onClick={onDelete}
                                text={intl.formatMessage({
                                    id: 'modals.filters.label.delete',
                                    defaultMessage: 'DELETE',
                                })}
                                disabled={!selectedPhotos.length}
                            />
                        </span>
                    </div>
                }
            />
            {showPhotoFullScreen && (
                <LuxPhotoFullScreen
                    selectedImage={selectedImage}
                    tagList={tagList}
                    tagOnImageList={tagOnImageList}
                    newTagPosition={newTagPosition}
                    imageInfo={selectedImageInfo}
                    totalImage={uploadedImageList.length}
                    intl={intl}
                    onClose={onCloseFullScreen}
                    previousImage={previousImage}
                    nextImage={nextImage}
                    setEditLoading={setEditLoading}
                    disableTag
                    disableEdit
                    disableInfo
                />
            )}
        </div>
    )
}

const mapStateToProps = (state: any) => ({
    storesList: state[FILTERS_REDUCER_NAMESPACE].distributionList,
    albumDetails: state[GALLERY_REDUCER_NAMESPACE]?.newAlbum || new Album(),
})

const mapDispatchToProps = (dispatch: any) => ({
    setAlbumDetails: (data: any) => dispatch(setAlbumDetails(data)),
})

export default connect(mapStateToProps, mapDispatchToProps)(AddedPhotosAccordion)
